import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ContractMessage, ContractState } from '../../../../types/ContractMessage';
import { VesselMessage } from '../../../../types/VesselMessage';
import { TableBuilder } from '../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../cdl/TableBuilder/TableBuilderColumn';
import { Tag } from '../../../cdl/Tag/Tag';
import { Text } from '../../../cdl/Text/Text';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { queries } from '../../../common/api/queryKeys/queries';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { useRole } from '../../../common/hooks/useRole';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../common/ui/Box';
import { ContractStatusTag } from '../../../contracts/overview/components/ContractStatusTag';
import { ContractTableValidityDisplay } from '../../../contracts/overview/components/ContractTableValidityDisplay';

import { ExcludeVesselFromContractModal } from './components/ExcludeVesselFromContractModal';
import { SetDefaultContractModal } from './components/SetDefaultContractModal';
import { VesselContractRowActionsMenu } from './components/VesselContractRowActionsMenu';
import { VesselContractsEmptyState } from './components/VesselContractsEmptyState';
import { useExcludeVesselFromContract } from './hooks/useExcludeVesselFromContract';
import { useVesselDefaultContractUpdate } from './hooks/useVesselDefaultContractUpdate';

const sortContracts = (contracts: ContractMessage[], defaultContractId?: string) => {
    return [...contracts].sort((contract, otherContract) => {
        if (contract.id === defaultContractId) {
            return -1;
        }
        if (otherContract.id === defaultContractId) {
            return 1;
        }

        return Date.parse(otherContract.dateCreated ?? '') - Date.parse(contract.dateCreated ?? '');
    });
};

interface VesselContractsTableProps {
    vessel: VesselMessage;
}

interface DefaultContractSelection {
    contract: ContractMessage;
    supplierChange: {
        currentSupplierName: string;
        newSupplierName: string;
    } | null;
}

export const VesselContractsTable = ({ vessel }: VesselContractsTableProps) => {
    const navigate = useNavigate();
    const role = useRole();
    const { addToast, addErrorToast } = useToasts();
    const defaultContractMutation = useVesselDefaultContractUpdate();
    const excludeMutation = useExcludeVesselFromContract();
    const [defaultContractSelection, setDefaultContractSelection] = useState<DefaultContractSelection | null>(null);
    const [contractToExclude, setContractToExclude] = useState<ContractMessage | null>(null);

    const contractsQuery = useQuery(
        queries.contracts.pagination({
            page: 0,
            size: 100,
            customerGroupIds: [vessel.customer.customerGroupId],
            customerIds: [vessel.customerId],
            states: [ContractState.ACTIVE, ContractState.AWARDED],
            vesselIds: [vessel.id],
        })
    );

    const contracts = sortContracts(contractsQuery.data?.content ?? [], vessel.defaultContractId);

    const handleSetDefaultConfirm = () => {
        if (!defaultContractSelection) {
            return;
        }

        defaultContractMutation.mutate(
            {
                vessel,
                contract: defaultContractSelection.contract,
            },
            {
                onSuccess: () => {
                    setDefaultContractSelection(null);
                    addToast(translate('vessel.contracts.defaultContract.updateSuccess'));
                },
                onError: () => {
                    addErrorToast(translate('vessel.contracts.defaultContract.updateError'));
                },
            }
        );
    };

    const handleSetDefault = (contract: ContractMessage) => {
        const supplierWillChange = Boolean(contract.supplierId && contract.supplierId !== vessel.defaultSupplierId);

        setDefaultContractSelection({
            contract,
            supplierChange: supplierWillChange
                ? {
                      currentSupplierName: vessel.defaultSupplier?.name ?? Characters.EN_DASH,
                      newSupplierName: contract.supplier?.name ?? Characters.EN_DASH,
                  }
                : null,
        });
    };

    const handleClearDefault = () => {
        defaultContractMutation.mutate(
            {
                vessel,
                contract: null,
            },
            {
                onSuccess: () => {
                    addToast(translate('vessel.contracts.defaultContract.updateSuccess'));
                },
                onError: () => {
                    addErrorToast(translate('vessel.contracts.defaultContract.updateError'));
                },
            }
        );
    };

    const handleExcludeConfirm = () => {
        if (!contractToExclude) {
            return;
        }

        excludeMutation.mutate(
            {
                vessel,
                contract: contractToExclude,
            },
            {
                onSuccess: () => {
                    setContractToExclude(null);
                    addToast(translate('vessel.contracts.excludeSuccess'));
                },
                onError: () => {
                    addErrorToast(translate('vessel.contracts.excludeError'));
                },
            }
        );
    };

    if (contractsQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (contractsQuery.isError) {
        return <AdvancedError />;
    }

    return (
        <>
            <Box display="grid" rowGap={12}>
                {contracts.length ? (
                    <>
                        <Text as="h2" variant="title">
                            {translate('vessel.contracts.title')}
                        </Text>

                        <TableBuilder
                            data={contracts}
                            grow={false}
                            isLoading={contractsQuery.isPlaceholderData && contractsQuery.isFetching}
                            onRowSelect={(contract) =>
                                navigate({
                                    to: '/contracts/$id',
                                    params: { id: contract.id },
                                    state: { vesselId: vessel.id },
                                })
                            }
                        >
                            <TableBuilderColumn<ContractMessage>
                                header={translate('contracts.labels.name')}
                                width="30%"
                            >
                                {(contract) => (
                                    <Box display="flex" alignItems="center" gap={8}>
                                        <Text fontWeight={600}>{contract.name}</Text>
                                        {contract.id === vessel.defaultContractId ? (
                                            <Tag>{translate('vessel.default')}</Tag>
                                        ) : null}
                                    </Box>
                                )}
                            </TableBuilderColumn>
                            <TableBuilderColumn<ContractMessage>
                                header={translate('contracts.labels.supplier')}
                                width="30%"
                                truncate
                            >
                                {(contract) => contract.supplier?.name}
                            </TableBuilderColumn>
                            <TableBuilderColumn<ContractMessage>
                                header={translate('contracts.labels.validity')}
                                width="35%"
                            >
                                {(contract) => <ContractTableValidityDisplay contract={contract} />}
                            </TableBuilderColumn>
                            <TableBuilderColumn<ContractMessage>
                                header={translate('contracts.labels.status')}
                                width="15%"
                            >
                                {(contract) => <ContractStatusTag status={contract.state} />}
                            </TableBuilderColumn>
                            <TableBuilderColumn<ContractMessage> header="" width="5%" interactive>
                                {(contract) => (
                                    <Box display="flex" justifyContent="flex-end">
                                        {role.hasAnyAdminRights() ? (
                                            <VesselContractRowActionsMenu
                                                contract={contract}
                                                vessel={vessel}
                                                onClearDefault={handleClearDefault}
                                                onSetDefault={handleSetDefault}
                                                onExclude={setContractToExclude}
                                            />
                                        ) : null}
                                    </Box>
                                )}
                            </TableBuilderColumn>
                        </TableBuilder>
                    </>
                ) : (
                    <VesselContractsEmptyState />
                )}
            </Box>

            {defaultContractSelection ? (
                <SetDefaultContractModal
                    contract={defaultContractSelection.contract}
                    supplierChange={defaultContractSelection.supplierChange}
                    isSubmitting={defaultContractMutation.isPending}
                    onConfirm={handleSetDefaultConfirm}
                    onDismiss={() => setDefaultContractSelection(null)}
                />
            ) : null}
            {contractToExclude ? (
                <ExcludeVesselFromContractModal
                    contract={contractToExclude}
                    isSubmitting={excludeMutation.isPending}
                    onConfirm={handleExcludeConfirm}
                    onDismiss={() => setContractToExclude(null)}
                />
            ) : null}
        </>
    );
};
